import React, { useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';
import { FREELANCE_FEATURE_FLAG } from '../constants/featureFlags';

const Container = styled.div`
  padding: 10px;
`;

const SectionLabel = styled.div`
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  margin: 36px 0 12px;
  color: ${props => (props.theme === 'light' ? '#999' : '#777')};

  &:first-of-type {
    margin-top: 8px;
  }
`;

const Hero = styled.div`
  margin-bottom: 8px;
`;

const Headline = styled.h2`
  font-size: 2rem;
  line-height: 1.2;
  margin-bottom: 12px;
  color: ${props => (props.theme === 'light' ? '#333' : '#e0e0e0')};

  .highlight {
    color: var(--highlight-color-${props => (props.theme === 'light' ? 'light' : 'dark')});
  }
`;

const Subhead = styled.p`
  font-size: 1.15rem;
  line-height: 1.6;
  margin-bottom: 16px;
  color: ${props => (props.theme === 'light' ? '#555' : '#c0c0c0')};
`;

const Positioning = styled.p`
  line-height: 1.7;
  margin-bottom: 22px;
  color: ${props => (props.theme === 'light' ? '#666' : '#b0b0b0')};

  strong {
    color: var(--highlight-color-${props => (props.theme === 'light' ? 'light' : 'dark')});
    font-weight: 600;
  }
`;

const PrimaryButton = styled.button`
  display: inline-block;
  background: var(--highlight-color-${props => (props.theme === 'light' ? 'light' : 'dark')});
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 22px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: var(--highlight-hover-${props => (props.theme === 'light' ? 'light' : 'dark')});
    transform: translateY(-2px);
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  border: 1px solid ${props => (props.theme === 'light' ? '#eee' : '#333')};
  border-radius: 8px;
  padding: 16px;
  background-color: ${props => (props.theme === 'light' ? 'white' : '#1e1e1e')};
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, ${props => (props.theme === 'light' ? '0.1' : '0.3')});
    border-color: var(--highlight-color-${props => (props.theme === 'light' ? 'light' : 'dark')});
  }
`;

const CardTitle = styled.h3`
  font-size: 1.05rem;
  margin-bottom: 8px;
  color: ${props => (props.theme === 'light' ? '#333' : '#e0e0e0')};
`;

const CardText = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${props => (props.theme === 'light' ? '#666' : '#b0b0b0')};
`;

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Step = styled.div`
  display: flex;
  gap: 16px;
  align-items: baseline;
`;

const StepNumber = styled.span`
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--highlight-color-${props => (props.theme === 'light' ? 'light' : 'dark')});
  flex-shrink: 0;
`;

const StepBody = styled.div``;

const StepTitle = styled.div`
  font-weight: 600;
  margin-bottom: 3px;
  color: ${props => (props.theme === 'light' ? '#333' : '#e0e0e0')};
`;

const StepText = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${props => (props.theme === 'light' ? '#666' : '#b0b0b0')};
`;

const AudienceList = styled.ul`
  margin: 0 0 8px;
  padding-left: 20px;
  line-height: 1.7;
  color: ${props => (props.theme === 'light' ? '#555' : '#b0b0b0')};
`;

const FaqItem = styled.div`
  border-bottom: 1px solid ${props => (props.theme === 'light' ? '#eee' : '#333')};
  padding: 14px 0;
`;

const FaqQuestion = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  color: ${props => (props.theme === 'light' ? '#333' : '#e0e0e0')};
`;

const FaqAnswer = styled.div`
  font-size: 0.92rem;
  line-height: 1.6;
  color: ${props => (props.theme === 'light' ? '#666' : '#b0b0b0')};
`;

const Form = styled.form`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 560px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 0.92rem;
  color: ${props => (props.theme === 'light' ? '#333' : '#e0e0e0')};
`;

const fieldStyles = props => `
  padding: 10px;
  border-radius: 5px;
  border: 1px solid ${props.theme === 'light' ? '#ccc' : '#444'};
  background: ${props.theme === 'light' ? 'white' : '#232323'};
  color: ${props.theme === 'light' ? '#333' : '#e0e0e0'};
  font-size: 1rem;
  font-family: inherit;
  transition: border 0.2s;
  &:focus {
    border-color: var(--highlight-color-${props.theme === 'light' ? 'light' : 'dark'});
    outline: none;
  }
`;

const Input = styled.input`
  ${fieldStyles}
`;

const Select = styled.select`
  ${fieldStyles}
`;

const TextArea = styled.textarea`
  ${fieldStyles}
  min-height: 120px;
  resize: vertical;
`;

const SubmitButton = styled.button`
  background: var(--highlight-color-${props => (props.theme === 'light' ? 'light' : 'dark')});
  color: white;
  border: none;
  border-radius: 5px;
  padding: 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  &:hover {
    background: var(--highlight-hover-${props => (props.theme === 'light' ? 'light' : 'dark')});
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div`
  margin-top: 16px;
  color: ${props => (props.success ? 'green' : 'red')};
  font-weight: 500;
`;

const services = [
  {
    title: 'Find the real problem',
    text: "Before a line of code: what's actually worth building, who it's for, and what to cut. The product judgment that keeps you from building the wrong thing.",
  },
  {
    title: 'Design the experience',
    text: 'Turn a rough idea into clear flows and an interface people understand without a manual. Intuitive by default.',
  },
  {
    title: 'Build & ship it',
    text: 'Full-stack development and deployment — web apps, browser extensions, tools — put in front of real users, not just a prototype.',
  },
  {
    title: 'Iterate with data',
    text: 'Analytics, feedback loops, and a clear next version. Ship, learn, improve — instead of guessing.',
  },
];

const steps = [
  { n: '01', title: 'Intro call', text: 'A free, no-pressure chat about your idea, your goal, and whether I’m the right fit. Honest take, either way.' },
  { n: '02', title: 'Scope & plan', text: 'I turn the idea into a clear scope: what the MVP is, what it isn’t, a timeline, and a straight estimate.' },
  { n: '03', title: 'Build & ship', text: 'I design and build it, with regular updates so you always know where things stand. No black box.' },
  { n: '04', title: 'Launch & iterate', text: 'Get it in front of users, watch how they actually use it, and decide the next move together.' },
];

const faqs = [
  {
    q: 'How much does it cost?',
    a: 'Every project is different. I work both project-based and hourly — after a quick call I’ll give you a straight estimate and an honest sense of what’s realistic for your budget.',
  },
  {
    q: 'I’m non-technical — is that a problem?',
    a: 'Not at all — that’s exactly who this is for. You bring the idea and the domain knowledge; I handle the product decisions and the building. No jargon required.',
  },
  {
    q: 'How fast can you start, and how fast can you ship?',
    a: 'It depends on scope, but MVPs are meant to be small and fast. We’ll define the smallest version that proves the idea and get it shipped, rather than spending months on v1.',
  },
  {
    q: 'What kinds of things do you build?',
    a: 'Web apps, browser extensions, internal tools, and the like — anything that gets a real product into users’ hands. If it’s outside what I can do well, I’ll tell you.',
  },
];

const WorkWithMeSection = () => {
  const { theme } = useContext(ThemeContext);
  const posthog = usePostHog();
  const isEnabled = useFeatureFlagEnabled(FREELANCE_FEATURE_FLAG) === true;
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project_description: '',
    timeline: '',
    start_date: '',
    end_date: '',
    budget: '',
    urgency: '',
  });
  const [status, setStatus] = useState({ submitting: false, success: '', error: '' });
  const [hasStarted, setHasStarted] = useState(false);

  // Belt-and-suspenders: never render content if the flag is off.
  if (!isEnabled) return null;

  const scrollToForm = source => {
    posthog?.capture('work_with_me_cta_clicked', { source });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = e => {
    if (!hasStarted) {
      posthog?.capture('project_inquiry_started', { first_field: e.target.name });
      setHasStarted(true);
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    posthog?.capture('project_inquiry_submitted');
    setStatus({ submitting: true, success: '', error: '' });
    try {
      // Same-origin POST to our own Cloud Function rather than straight to
      // PostHog: eu.i.posthog.com is on the default ad-blocker lists, and a
      // silently dropped lead is far worse than a silently dropped pageview.
      // The function records the response with posthog-node, server to server.
      const res = await fetch('/api/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          // Carries the visitor's analytics identity across so the inquiry
          // attaches to the same person as their pageviews. Absent when
          // posthog-js itself was blocked; the function falls back to the email.
          distinct_id: posthog?.get_distinct_id?.() || '',
          _gotcha: e.target.elements._gotcha?.value || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        posthog?.capture('project_inquiry_success');
        setStatus({ submitting: false, success: 'Thanks! Your project details are in — I’ll get back to you soon.', error: '' });
        setFormData({ name: '', email: '', project_description: '', timeline: '', start_date: '', end_date: '', budget: '', urgency: '' });
      } else {
        const errorMsg = data.error || 'Something went wrong. Please try again.';
        posthog?.capture('project_inquiry_error', { error: errorMsg });
        setStatus({ submitting: false, success: '', error: errorMsg });
      }
    } catch (err) {
      posthog?.capture('project_inquiry_error', { error: 'network_error' });
      setStatus({ submitting: false, success: '', error: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <Container>
      <Hero>
        <Headline theme={theme}>
          From idea to <span className="highlight">shipped MVP</span>
        </Headline>
        <Subhead theme={theme}>
          I help founders turn a rough idea into a real product that’s in users’ hands — fast.
        </Subhead>
        <Positioning theme={theme}>
          Most people you can hire are either a <strong>product person who can’t build</strong>, or an{' '}
          <strong>engineer who needs to be told exactly what to make</strong>. I’m both. As a Product Manager
          I figure out what’s actually worth building and what to cut; with a full-stack background I design it
          and ship it myself. One person, idea to launch — no translation layer, no agency overhead.
        </Positioning>
        <PrimaryButton theme={theme} type="button" onClick={() => scrollToForm('hero')}>
          Tell me about your idea
        </PrimaryButton>
      </Hero>

      <SectionLabel theme={theme}>{'// what_i_do'}</SectionLabel>
      <CardGrid>
        {services.map(s => (
          <Card key={s.title} theme={theme}>
            <CardTitle theme={theme}>{s.title}</CardTitle>
            <CardText theme={theme}>{s.text}</CardText>
          </Card>
        ))}
      </CardGrid>

      <SectionLabel theme={theme}>{'// how_it_works'}</SectionLabel>
      <Steps>
        {steps.map(step => (
          <Step key={step.n}>
            <StepNumber theme={theme}>{step.n}</StepNumber>
            <StepBody>
              <StepTitle theme={theme}>{step.title}</StepTitle>
              <StepText theme={theme}>{step.text}</StepText>
            </StepBody>
          </Step>
        ))}
      </Steps>

      <SectionLabel theme={theme}>{'// who_its_for'}</SectionLabel>
      <AudienceList theme={theme}>
        <li>Non-technical founders with an idea and no one to build it.</li>
        <li>People with a validated problem who need a first product, fast.</li>
        <li>Anyone who wants one accountable person — not an agency or a hand-off chain.</li>
      </AudienceList>

      <SectionLabel theme={theme}>{'// faq'}</SectionLabel>
      {faqs.map(f => (
        <FaqItem key={f.q} theme={theme}>
          <FaqQuestion theme={theme}>{f.q}</FaqQuestion>
          <FaqAnswer theme={theme}>{f.a}</FaqAnswer>
        </FaqItem>
      ))}

      <SectionLabel theme={theme} ref={formRef}>{'// start_a_project'}</SectionLabel>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <Row>
          <InputGroup>
            <Label theme={theme} htmlFor="wwm-name">Name</Label>
            <Input theme={theme} type="text" id="wwm-name" name="name" value={formData.name} onChange={handleChange} required autoComplete="off" />
          </InputGroup>
          <InputGroup>
            <Label theme={theme} htmlFor="wwm-email">Email</Label>
            <Input theme={theme} type="email" id="wwm-email" name="email" value={formData.email} onChange={handleChange} required autoComplete="off" />
          </InputGroup>
        </Row>

        <InputGroup>
          <Label theme={theme} htmlFor="wwm-description">Project description</Label>
          <TextArea theme={theme} id="wwm-description" name="project_description" value={formData.project_description} onChange={handleChange} placeholder="What are you trying to build, and what problem does it solve?" required />
        </InputGroup>

        <Row>
          <InputGroup>
            <Label theme={theme} htmlFor="wwm-timeline">Timeline</Label>
            <Input theme={theme} type="text" id="wwm-timeline" name="timeline" value={formData.timeline} onChange={handleChange} placeholder="e.g. a few weeks, this quarter" required autoComplete="off" />
          </InputGroup>
          <InputGroup>
            <Label theme={theme} htmlFor="wwm-urgency">Urgency</Label>
            <Select theme={theme} id="wwm-urgency" name="urgency" value={formData.urgency} onChange={handleChange} required>
              <option value="" disabled>Select…</option>
              <option value="Low">Low — just exploring</option>
              <option value="Medium">Medium — planning ahead</option>
              <option value="High">High — ready to go</option>
              <option value="ASAP">ASAP — needed yesterday</option>
            </Select>
          </InputGroup>
        </Row>

        <Row>
          <InputGroup>
            <Label theme={theme} htmlFor="wwm-start">Estimated start date</Label>
            <Input theme={theme} type="date" id="wwm-start" name="start_date" value={formData.start_date} onChange={handleChange} />
          </InputGroup>
          <InputGroup>
            <Label theme={theme} htmlFor="wwm-end">Estimated end date</Label>
            <Input theme={theme} type="date" id="wwm-end" name="end_date" value={formData.end_date} onChange={handleChange} />
          </InputGroup>
        </Row>

        <InputGroup>
          <Label theme={theme} htmlFor="wwm-budget">Estimated budget</Label>
          <Select theme={theme} id="wwm-budget" name="budget" value={formData.budget} onChange={handleChange}>
            <option value="">Prefer not to say</option>
            <option value="< $2k">Less than $2k</option>
            <option value="$2k–$5k">$2k–$5k</option>
            <option value="$5k–$10k">$5k–$10k</option>
            <option value="$10k–$25k">$10k–$25k</option>
            <option value="$25k+">$25k+</option>
          </Select>
        </InputGroup>

        {/* Honeypot field for spam protection; checked server-side */}
        <input type="text" name="_gotcha" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

        <SubmitButton theme={theme} type="submit" disabled={status.submitting}>
          {status.submitting ? 'Sending…' : 'Send project details'}
        </SubmitButton>
      </Form>
      {status.success && <Message theme={theme} success>{status.success}</Message>}
      {status.error && <Message theme={theme}>{status.error}</Message>}
    </Container>
  );
};

export default WorkWithMeSection;
